/**
 * @module UseDemoModeCeremony
 * @purpose PIN + current-password submission state for the Demo Mode enable/disable ceremony.
 * @depends services/platform (toggleDemoMode, getDemoModeErrorCode)
 * @dependedOnBy pages/platform-settings/DemoModeCeremonyModal
 *
 * @rules
 * - Performance: single in-flight submission at a time
 * - State: local useState for pin/password/submitting/errorCode
 * - Errors: never throws -- failures resolve to `errorCode`, translated by the caller.
 *   The raw backend code is kept only as a lookup key; it must never be rendered directly
 */

import { useCallback, useState } from 'react'
import { toggleDemoMode, getDemoModeErrorCode } from '../../services/platform'
import type { DemoModeErrorCode, PlatformStatus } from '../../types/platform'

export type DemoModeCeremonyErrorCode = DemoModeErrorCode | 'unknown'

export interface UseDemoModeCeremonyResult {
  pin: string
  setPin: (value: string) => void
  password: string
  setPassword: (value: string) => void
  submitting: boolean
  errorCode: DemoModeCeremonyErrorCode | null
  submit: () => Promise<void>
  reset: () => void
}

interface UseDemoModeCeremonyParams {
  targetEnabled: boolean
  onSuccess: (status: PlatformStatus) => void
}

/**
 * Manage the PIN + current-password ceremony that flips platform Demo Mode.
 *
 * @behaviour `submit` is a no-op while already submitting. On success, `reset` clears the
 *            form and `onSuccess` is invoked with the new `PlatformStatus`. On failure,
 *            `errorCode` is set from the backend error code (or `'unknown'`) and the form
 *            values are left intact so the caller can correct a typo without re-entering both.
 * @errors Never throws -- all failures resolve into `errorCode`.
 */
export function useDemoModeCeremony({
  targetEnabled,
  onSuccess,
}: UseDemoModeCeremonyParams): UseDemoModeCeremonyResult {
  const [pin, setPin] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorCode, setErrorCode] = useState<DemoModeCeremonyErrorCode | null>(null)

  const reset = useCallback((): void => {
    setPin('')
    setPassword('')
    setSubmitting(false)
    setErrorCode(null)
  }, [])

  const submit = useCallback(async (): Promise<void> => {
    if (submitting) {
      return
    }
    setSubmitting(true)
    setErrorCode(null)
    try {
      const status = await toggleDemoMode({
        enabled: targetEnabled,
        pin,
        currentPassword: password,
      })
      onSuccess(status)
      reset()
    } catch (err) {
      setErrorCode(getDemoModeErrorCode(err) ?? 'unknown')
      setSubmitting(false)
    }
  }, [submitting, targetEnabled, pin, password, onSuccess, reset])

  return { pin, setPin, password, setPassword, submitting, errorCode, submit, reset }
}
